
import React, { useState, useEffect, useMemo } from "react";
import { Paper, Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Typography, Modal, Button, TablePagination } from '@mui/material';
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../context/SuperPickemContext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DateTime } from "luxon";
import CloseIcon from '@mui/icons-material/Close';
import { getTeamLogo } from "../../../utils/SportsData";
import "./DailyPickemPicks.scss";
import { getRankSuffix } from "../../../utils/GetRankSuffix";

const DailyPickemPicks = ({filteredDatesReverse}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    
    const {
        league,
        gameId,
        myUserId,
        leaderboardData,
        pickState,
        isUserInThisPool,
        myLeaderboardInfo,
    } = useUserAndGameContext();

    const {
        teams,
        gameSchedule,
    } = useSuperPickemContext();

    // If you need to use your local intervalOptions for testing:
    const myInfo = myLeaderboardInfo?.myInfo || {};
    
    
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [sortBy, setSortBy] = useState("rank");
    const [sortOrder, setSortOrder] = useState("asc");

    const today = DateTime.now().setZone("America/New_York").startOf('day');
    const filteredSchedule = useMemo(() => {
        //console.log("Today:", today);
        const todayFormatted = today.toFormat("yyyy-MM-dd'T'HH:mm:ss");
        return gameSchedule?.filter(game => {
            return (
                game?.Day === todayFormatted &&
                game?.Status !== "NotNecessary"
            );
        });
    }, [gameSchedule]);
    //console.log("Filtered Schedule:", filteredSchedule, gameSchedule);

    // Are all of the games in the past?
    const allGamesInPast = useMemo(() => {
        return filteredSchedule?.every(game => {
            const timeNowInNewYork = DateTime.now().setZone("America/New_York");
            const gameDateTime = DateTime.fromISO(game.DateTime, { zone: "America/New_York" });
            return gameDateTime < timeNowInNewYork;
        });
    }, [filteredSchedule]);
    //console.log("All Games In Past:", allGamesInPast);

    function hasGameStarted(gameId) {
        const game = filteredSchedule?.find(game => game.GlobalGameID === gameId);
        //console.log("Game:", game, gameId);
        if (!game) return false;
        const gameDateTime = DateTime.fromISO(game.DateTime, { zone: "America/New_York" });
        const timeNowInNewYork = DateTime.now().setZone("America/New_York");
        return gameDateTime < timeNowInNewYork;
    }

    // Handle sorting
    const handleSort = () => {
        setSortBy("rank");
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    };

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Reset page when data or rows per page changes
    useEffect(() => {
        setPage(0);
    }, [leaderboardData, rowsPerPage]);

    
    // Format date for display (e.g., 4/21)
    const formatDateForDisplay = (dateStr) => {
        const date = DateTime.fromISO(dateStr);
        return `${date.month}/${date.day}`;
    };

    // Sort leaderboard data
    const sortedLeaderboard = leaderboardData?.sort((a, b) => {
        if (sortBy === "rank") {
            return sortOrder === "asc" ? a.dailyPicksRank - b.dailyPicksRank : b.dailyPicksRank - a.dailyPicksRank;
        }
        return 0;
    });

    // Find user's entry in the leaderboard
    const userEntries = leaderboardData?.filter(entry => entry.userId === myUserId);

    // Paginate data
    const paginatedData = sortedLeaderboard?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Check if a date is in the past
    const isDateInPast = (dateStr) => {
        const date = DateTime.fromISO(dateStr);
        return date < today;
    };

    // Check if a date is today
    const isDateToday = (dateStr) => {
        const date = DateTime.fromISO(dateStr);
        return date.hasSame(today, 'day');
    };

    // Format spread value
    const formatSpread = (spread) => {
        if (spread === undefined || spread === null) return '';
        if (spread === 0) return 'Pk';
        return spread > 0 ? `+${spread}` : `${spread}`;
    };

    // Function to determine position adjustment based on spread length
const getSpreadPosition = (spread) => {
    const formattedSpread = formatSpread(spread);
    const charCount = formattedSpread.length;
    
    // Adjust position based on character count
    if (charCount > 4) {
      return isSmallScreen ? '0px' : isMobile ? '0px' : '2px';
    } else if (charCount > 3) {
      return isSmallScreen ? '4px' : isMobile ? '1px' : '6px';
    } else {
      return isSmallScreen ? '6px' : isMobile ? (filteredDatesReverse?.length > 3 ? '3px' : '6px') : filteredDatesReverse?.length > 3 ? '8px' : '10px';
    }
  };

    return (
        <Box>
            <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
                <TablePagination
                    rowsPerPageOptions={[50, 100]}
                    component="div"
                    count={sortedLeaderboard?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={Math.min(page, Math.max(0, Math.ceil((sortedLeaderboard?.length || 0) / rowsPerPage) - 1))}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows"
                    sx={{
                        width: '90%',
                        backgroundColor: 'white',
                        '& .MuiTablePagination-toolbar': {
                            color: '#002129',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
                            color: '#002129',
                        }
                    }}
                />
            </Box>

            <Box sx={{ 
                width: '100%', 
                maxWidth: '1250px',
                overflowX: 'hidden',
                margin: '0 auto !important',
                justifyContent: 'center',
                display: 'flex',
            }}>
                <TableContainer
                    component={Paper}
                    className="daily-pickem-table-container"
                    sx={{
                        width: `calc(${isMobile ? '175px' : '200px'} + ${filteredDatesReverse.length * (isMobile ? 100 : 125)}px)`,
                        maxWidth: '1250px',
                        margin: '0 auto !important',
                        borderRadius: '0',
                        overflowX: 'auto',
                        position: 'relative',
                    }}
                >
                    <Table sx={{ 
                        tableLayout: 'fixed',
                        width: `calc(${isMobile ? '175px' : '200px'} + ${filteredDatesReverse.length * 100}px)`
                    }}>
                        {/* User's row above the header - similar to SurvivorLeaderboard */}
                        {userEntries?.length >= 1 && (
                            <TableHead sx={{ backgroundColor: '#F0F1F1' }}>
                                {userEntries.map((userEntry) => (
                                    <TableRow key={userEntry.username}>
                                        <TableCell 
                                            sx={{
                                                backgroundColor: '#F0F1F1',
                                                zIndex: 3,
                                                width: isMobile ? '175px' : '200px',
                                                minWidth: isMobile ? '175px' : '200px',
                                                maxWidth: isMobile ? '175px' : '200px',
                                                padding: '4px 16px 4px 16px',
                                                position: 'sticky',
                                                left: 0,
                                            }}
                                        >
                                            <Typography
                                                className='daily-pickem-username-text'
                                                sx={{ fontWeight: 'bold' }}
                                            >
                                                {userEntry.username}
                                            </Typography>
                                            <Typography
                                                className='daily-pickem-rank-text'
                                                sx={{ fontSize: '12px', color: '#002129', fontWeight: '600' }}
                                            >
                                                Pick 'Em Pts: {userEntry.dailyPicksPoints || 0} ({getRankSuffix(userEntry.dailyPicksRank) || 'N/A'})
                                            </Typography>
                                        </TableCell>
                                        
                                        {filteredDatesReverse.map((dateStr, dateIndex) => {
                                            // Find user's pick for this date
                                            const pick = pickState?.find(p => {
                                                const pickDate = DateTime.fromISO(p.interval);
                                                const compareDate = DateTime.fromISO(dateStr);
                                                return pickDate.hasSame(compareDate, 'day');
                                            });
                                            
                                            // Check date status
                                            const isPastDate = isDateInPast(dateStr);
                                            const isToday = isDateToday(dateStr);
                                            const showPick = true; // Always show user's picks in their row
                                            
                                            return (
                                                <TableCell
                                                    key={dateIndex}
                                                    sx={{
                                                        textAlign: 'center',
                                                        minWidth: isMobile ? '100px' : '125px',
                                                        width: isMobile ? '100px' : '125px',
                                                        padding: 0,
                                                    }}
                                                >
                                                    {pick && showPick ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '2px',
                                                                height: '100%',
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            <Box
                                                                className='daily-pickem-pick-box'
                                                                sx={{
                                                                    position: 'relative',
                                                                    border: pick.result === 'win' ? '2px solid #00AA72' : 
                                                                            (pick.result === 'loss' ? '2px solid #CC293C' : 
                                                                            (pick.result === 'push' ? '2px solid #5A5B5C' : '1px solid #002129')),
                                                                    background: pick.result === 'win' ? '#CCEEE3' : 
                                                                            (pick.result === 'loss' ? '#F5D4D8' : 
                                                                            (pick.result === 'push' ? '#5A5B5C80' : '#fff')),
                                                                    height: isMobile ? '32px' : '40px',
                                                                    width: isMobile ? '32px' : '40px',
                                                                    margin: '0 auto 0 auto',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRadius: '8px',
                                                                }}
                                                            >
                                                                {pick.teamId ? (
                                                                    <img
                                                                        src={getTeamLogo(league, pick.teamId, teams)}
                                                                        alt={`Team ${pick.teamId}`}
                                                                        style={{
                                                                            width: '90%',
                                                                            height: '90%',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <CloseIcon sx={{ color: '#CC293C' }} />
                                                                )}
                                                            </Box>
                                                            
                                                            {pick.spread !== undefined && pick.teamId && (
                                                                <Typography
                                                                    className='daily-pickem-spread'
                                                                    sx={{
                                                                        fontSize: isMobile ? '12px' : '14px',
                                                                        fontWeight: 'bold',
                                                                        color: '#002129',
                                                                        position: 'absolute',
                                                                        right: getSpreadPosition(pick.spread),
                                                                    }}
                                                                >
                                                                    {formatSpread(pick.spread)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        // Empty or no pick cell
                                                        isPastDate ? (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    position: 'relative',
                                                                }}
                                                            >
                                                                <Box
                                                                    className='daily-pickem-no-pick-box'
                                                                    sx={{
                                                                        height: isMobile ? '32px' : '40px',
                                                                        width: isMobile ? '32px' : '40px',
                                                                        margin: '0 auto',
                                                                        border: '2px solid #CC293C',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: 'transparent',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <CloseIcon sx={{ color: '#CC293C' }} />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    position: 'relative',
                                                                }}
                                                            >
                                                                <Box
                                                                    className='daily-pickem-empty-box'
                                                                    sx={{
                                                                        height: isMobile ? '32px' : '40px',
                                                                        width: isMobile ? '32px' : '40px',
                                                                        margin: '0 auto',
                                                                        border: '1px solid #ccc',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: '#FFF',
                                                                    }}
                                                                />
                                                            </Box>
                                                        )
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHead>
                        )}
                        
                        {/* Main table header */}
                        <TableHead sx={{ backgroundColor: '#002129' }}>
                            <TableRow>
                                <TableCell
                                    className='daily-pickem-table-header'
                                    onClick={handleSort}
                                    sx={{ 
                                        zIndex: 3, 
                                        backgroundColor: '#002129',
                                        color: 'white', 
                                        fontWeight: 'bold',
                                        width: isMobile ? '175px' : '200px',
                                        minWidth: isMobile ? '175px' : '200px',
                                        maxWidth: isMobile ? '175px' : '200px',
                                        position: 'sticky',
                                        left: 0,
                                        pointerEvents: 'none',
                                    }}
                                >
                                    STANDINGS
                                </TableCell>
                                {filteredDatesReverse.map((dateStr, index) => (
                                    <TableCell
                                        className='daily-pickem-table-date-columns'
                                        key={index}
                                        sx={{
                                            color: 'white',
                                            textAlign: 'center',
                                            minWidth: isMobile ? '100px' : '125px',
                                            width: isMobile ? '100px' : '125px',
                                            padding: '6px 3px',
                                        }}
                                    >
                                        <div>
                                            {formatDateForDisplay(dateStr)}
                                        </div>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        
                        {/* Table body - other users */}
                        <TableBody>
                            {paginatedData?.map((user, userIndex) => (
                                <TableRow
                                    key={`${user.userId}-${userIndex}`}
                                    sx={{
                                        backgroundColor: user.userId === myUserId
                                            ? '#FFF6DA'
                                            : userIndex % 2 === 0
                                                ? '#FFFFFF'
                                                : '#F0F1F1',
                                    }}
                                >
                                    <TableCell 
                                        className="daily-pickem-username-column"
                                        sx={{
                                            width: isMobile ? '175px' : '200px',
                                            minWidth: isMobile ? '175px' : '200px',
                                            maxWidth: isMobile ? '175px' : '200px',
                                            zIndex: 3,
                                            backgroundColor: user.userId === myUserId
                                                ? '#FFF6DA'
                                                : userIndex % 2 === 0
                                                    ? '#FFFFFF'
                                                    : '#F0F1F1',
                                            position: 'sticky',
                                            left: 0,
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <Typography
                                            className='daily-pickem-username-text'
                                            sx={{ fontWeight: 'bold' }}
                                        >
                                            {user.username}
                                        </Typography>
                                        <Typography
                                            className='daily-pickem-rank-text'
                                            sx={{ fontSize: '12px', color: '#002129', fontWeight: '600' }}
                                        >
                                            Pick 'Em Pts: {user.dailyPicksPoints || 0} ({getRankSuffix(user.dailyPicksRank) || 'N/A'})
                                        </Typography>
                                    </TableCell>
                                    
                                    {filteredDatesReverse.map((dateStr, dateIndex) => {
                                        const isThisMyRow = user.userId === myUserId;
                                        // Find the pick for this date if it exists
                                        const pick = isThisMyRow ?
                                            pickState?.find(p => {
                                                const pickDate = DateTime.fromISO(p.interval);
                                                const compareDate = DateTime.fromISO(dateStr);
                                                return pickDate.hasSame(compareDate, 'day');
                                            }) :
                                            user.picks?.find(p => {
                                                const pickDate = DateTime.fromISO(p.interval);
                                                const compareDate = DateTime.fromISO(dateStr);
                                                return pickDate.hasSame(compareDate, 'day');
                                        });

                                        // Check if this date is in the past or is today
                                        const isPastDate = isDateInPast(dateStr);
                                        const isToday = isDateToday(dateStr);
                                        const isFutureDate = !isPastDate && !isToday;
                                        
                                        // If it's today's game and there's a pick, check if the game has started
                                        // For future dates, never show the pick
                                        const shouldShowPick = (isToday && pick && hasGameStarted(pick.gameId)) || 
                                                               (isPastDate && pick) || isThisMyRow;

                                        return (
                                            <TableCell
                                                key={dateIndex}
                                                sx={{
                                                    textAlign: 'center',
                                                    minWidth: isMobile ? '100px' : '65px',
                                                    width: isMobile ? '100px' : '65px',
                                                    padding: 0,
                                                }}
                                            >
                                                {pick && shouldShowPick ? (
                                                    // User made a pick for this date and (it's not today OR it's today but the game has started)
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '2px',
                                                            height: '100%',
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <Box
                                                            className='daily-pickem-pick-box'
                                                            sx={{
                                                                position: 'relative',
                                                                border: pick.result === 'win' ? '2px solid #00AA72' : 
                                                                        (pick.result === 'loss' ? '2px solid #CC293C' : 
                                                                        (pick.result === 'push' ? '2px solid #5A5B5C' : '1px solid #002129')),
                                                                background: pick.result === 'win' ? '#CCEEE3' : 
                                                                        (pick.result === 'loss' ? '#F5D4D8' : 
                                                                        (pick.result === 'push' ? '#5A5B5C80' : '#fff')),
                                                                height: isMobile ? '32px' : '40px',
                                                                width: isMobile ? '32px' : '40px',
                                                                margin: '0 auto 0 auto',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '8px',
                                                            }}
                                                        >
                                                            {pick.teamId ? (
                                                                <img
                                                                    src={getTeamLogo(league, pick.teamId, teams)}
                                                                    alt={`Team ${pick.teamId}`}
                                                                    style={{
                                                                        width: '90%',
                                                                        height: '90%',
                                                                    }}
                                                                />
                                                            ) : (
                                                                <CloseIcon sx={{ color: '#CC293C' }} />
                                                            )}
                                                        </Box>
                                                        
                                                        {/* Spread placed to the right of the box */}
                                                        {pick.spread !== undefined && pick.teamId && (
                                                            <Typography
                                                                className='daily-pickem-spread'
                                                                sx={{
                                                                    fontSize: isMobile ? '12px' : '14px',
                                                                    fontWeight: 'bold',
                                                                    color: '#002129',
                                                                    position: 'absolute',
                                                                    right: getSpreadPosition(pick.spread),
                                                                }}
                                                            >
                                                                {formatSpread(pick.spread)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    // For today's games where the game hasn't started yet or future dates, show empty box
                                                    (isToday && pick && !hasGameStarted(pick.gameId)) || (isFutureDate && pick) ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            <Box
                                                                className='daily-pickem-empty-box'
                                                                sx={{
                                                                    height: isMobile ? '32px' : '40px',
                                                                    width: isMobile ? '32px' : '40px',
                                                                    margin: '0 auto',
                                                                    border: '1px solid #ccc',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: '#FFF',
                                                                }}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        // No pick for this date
                                                        (isPastDate || (isToday && allGamesInPast)) ? (
                                                            // Past date with no pick - show CloseIcon
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    position: 'relative',
                                                                }}
                                                            >
                                                                <Box
                                                                    className='daily-pickem-no-pick-box'
                                                                    sx={{
                                                                        height: isMobile ? '32px' : '40px',
                                                                        width: isMobile ? '32px' : '40px',
                                                                        margin: '0 auto',
                                                                        border: '2px solid #CC293C',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: 'transparent',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    <CloseIcon sx={{ color: '#CC293C' }} />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            // Future date with no pick - show empty box
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    position: 'relative',
                                                                }}
                                                            >
                                                                <Box
                                                                    className='daily-pickem-empty-box'
                                                                    sx={{
                                                                        height: isMobile ? '32px' : '40px',
                                                                        width: isMobile ? '32px' : '40px',
                                                                        margin: '0 auto',
                                                                        border: '1px solid #ccc',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: '#FFF',
                                                                    }}
                                                                />
                                                            </Box>
                                                        )
                                                    )
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
                <TablePagination
                    rowsPerPageOptions={[50, 100]}
                    component="div"
                    count={sortedLeaderboard?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={Math.min(page, Math.max(0, Math.ceil((sortedLeaderboard?.length || 0) / rowsPerPage) - 1))}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows"
                    sx={{
                        width: '90%',
                        backgroundColor: 'white',
                        '& .MuiTablePagination-toolbar': {
                            color: '#002129',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input, & .MuiTablePagination-actions': {
                            color: '#002129',
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

export default DailyPickemPicks;